import React, { useState, useEffect, useCallback } from 'react';
import { Target, Star, Gem, Sparkles, Pencil, Check, X, Loader2, Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getSharedMVVForUser, getMVVByOwnerId, updateUserMVVSection } from '../../services/firebase/missionVisionValues';
import { generateCompanyDirection } from '../../services/hrAiService';

const SECTION_META = {
  mission: { label: 'Mission', icon: Target, placeholder: 'What your company exists to do.' },
  vision: { label: 'Vision', icon: Star, placeholder: 'The future your company is working toward.' },
  values: { label: 'Core Values', icon: Gem },
};

const MAX_VALUES = 4;

// Prompts + examples shown inside the "Write with AI" modal for each section.
const AI_MODAL_META = {
  mission: {
    prompt: 'Briefly describe your mission, or the components you want to incorporate.',
    exampleLabel: 'Example — CQC Home',
    example: 'Our mission is to tangibly improve the life of our clients and employees.',
  },
  vision: {
    prompt: 'Briefly describe your vision, or the components you want to incorporate.',
    exampleLabel: 'Example — CQC Home',
    example: 'To be the largest remodeler in the Triangle.',
  },
  values: {
    prompt: 'Briefly describe the values you want to incorporate.',
  },
};

// 15 example core values (5 rows × 3 cols) shown as clickable inspiration.
const EXAMPLE_VALUES = [
  'Integrity', 'Excellence', 'Accountability',
  'Craftsmanship', 'Customer Focus', 'Teamwork',
  'Innovation', 'Transparency', 'Reliability',
  'Respect', 'Ownership', 'Safety',
  'Continuous Improvement', 'Passion', 'Communication',
];

// overrideOwnerId lets an internal viewer (CRM Portal View) load a specific
// client account's mission/vision/values (MVV docs are keyed by account id).
const CompanyDirectionCard = ({ businessName = '', overrideOwnerId = null }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [ownerId, setOwnerId] = useState(null);
  const [data, setData] = useState({ mission: '', vision: '', values: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'mission' | 'vision' | 'values'
  const [draftText, setDraftText] = useState('');
  const [draftValues, setDraftValues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(null); // section currently generating
  const [error, setError] = useState('');

  // "Write with AI" modal state
  const [aiModal, setAiModal] = useState(null); // 'mission' | 'vision' | 'values'
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiSelectedValues, setAiSelectedValues] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // When viewing a specific account (CRM), read that account's MVV doc
      // directly; otherwise resolve the logged-in user's shared owner id.
      let mvv;
      let oid;
      if (overrideOwnerId) {
        mvv = await getMVVByOwnerId(overrideOwnerId);
        oid = overrideOwnerId;
      } else {
        if (!currentUser?.uid) return;
        const shared = await getSharedMVVForUser(currentUser.uid, currentUser.email);
        mvv = shared.data;
        oid = shared.ownerId;
      }
      setOwnerId(oid);
      setData({
        mission: mvv?.mission || '',
        vision: mvv?.vision || '',
        values: Array.isArray(mvv?.values) ? mvv.values : [],
      });
    } catch (e) {
      console.error('Load company direction error:', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser, overrideOwnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const persist = async (section, value) => {
    if (!ownerId) return;
    setSaving(true);
    setError('');
    try {
      await updateUserMVVSection(ownerId, section, value);
      setData((d) => ({ ...d, [section]: value }));
    } catch (e) {
      console.error('Save company direction error:', e);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (section) => {
    setError('');
    setEditing(section);
    if (section === 'values') {
      setDraftValues(data.values.length ? data.values.map((v) => ({ ...v })) : [{ title: '', description: '' }]);
    } else {
      setDraftText(data[section] || '');
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraftText('');
    setDraftValues([]);
  };

  const saveText = async (section) => {
    await persist(section, draftText.trim());
    cancelEdit();
  };

  const saveValues = async () => {
    const cleaned = draftValues
      .map((v) => ({ title: (v.title || '').trim(), description: (v.description || '').trim() }))
      .filter((v) => v.title || v.description);
    await persist('values', cleaned);
    cancelEdit();
  };

  const runAi = async (section, instructions = '') => {
    setAiLoading(section);
    setError('');
    try {
      const res = await generateCompanyDirection({
        section,
        businessName,
        currentValue: section === 'values' ? '' : data[section],
        instructions,
      });
      if (section === 'values') {
        const vals = (res.values || []).slice(0, MAX_VALUES);
        if (editing === 'values') setDraftValues(vals.map((v) => ({ ...v })));
        else await persist('values', vals);
      } else {
        const text = res.content || '';
        if (editing === section) setDraftText(text);
        else await persist(section, text);
      }
      closeAiModal();
    } catch (e) {
      console.error('AI generate error:', e);
      setError(e?.message || 'AI generation failed. Please try again.');
    } finally {
      setAiLoading(null);
    }
  };

  const openAiModal = (section) => {
    setError('');
    setAiInstructions('');
    setAiSelectedValues([]);
    setAiModal(section);
  };

  const closeAiModal = () => {
    setAiModal(null);
    setAiInstructions('');
    setAiSelectedValues([]);
  };

  const toggleExampleValue = (title) => {
    setAiSelectedValues((prev) => {
      if (prev.includes(title)) return prev.filter((t) => t !== title);
      if (prev.length >= MAX_VALUES) return prev; // cap selection at 4
      return [...prev, title];
    });
  };

  const handleAiGenerate = async () => {
    if (!aiModal) return;
    const parts = [];
    if (aiInstructions.trim()) parts.push(aiInstructions.trim());
    if (aiModal === 'values') {
      if (aiSelectedValues.length) {
        parts.push(`Draw inspiration from these values: ${aiSelectedValues.join(', ')}.`);
      }
      parts.push(`Produce exactly ${MAX_VALUES} core values.`);
    }
    await runAi(aiModal, parts.join(' '));
  };

  const cardClass = `rounded-xl border p-6 ${darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`;
  const aiChip = (section) => (
    <button
      type="button"
      onClick={() => openAiModal(section)}
      disabled={aiLoading === section || saving}
      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-60"
      title="Write with AI"
    >
      {aiLoading === section ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      Write with AI
    </button>
  );
  const editBtn = (section) => (
    <button
      type="button"
      onClick={() => startEdit(section)}
      className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
      title={`Edit ${section}`}
    >
      <Pencil className="w-4 h-4" />
    </button>
  );

  const sectionHeader = (section, isEditing) => {
    const { label, icon: Icon } = SECTION_META[section];
    return (
      <div className="relative mb-4">
        <div className="absolute top-0 right-0 flex items-center gap-1">
          {aiChip(section)}
          {!isEditing && editBtn(section)}
        </div>
        <div className="flex flex-col items-center gap-2 pt-1">
          <span className="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
            <Icon className="w-7 h-7 text-red-600 dark:text-red-400" />
          </span>
          <h3 className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>{label}</h3>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className={cardClass}>
            <div className={`h-24 rounded-lg animate-pulse ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
          </div>
        ))}
      </div>
    );
  }

  const renderTextSection = (section) => {
    const meta = SECTION_META[section];
    const isEditing = editing === section;
    return (
      <div className={cardClass}>
        {sectionHeader(section, isEditing)}
        {isEditing ? (
          <div className="max-w-3xl mx-auto">
            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows="3"
              placeholder={meta.placeholder}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 text-gray-900'
              }`}
            />
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button type="button" onClick={() => saveText(section)} disabled={saving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          </div>
        ) : (
          <p className={`text-base leading-relaxed text-center max-w-3xl mx-auto ${data[section] ? (darkMode ? 'text-gray-200' : 'text-gray-700') : 'text-gray-400 italic'}`}>
            {data[section] || `No ${meta.label.toLowerCase()} yet — write one or generate with AI.`}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
    <div className="space-y-4">
      {error && !aiModal && <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderTextSection('mission')}
        {renderTextSection('vision')}
      </div>

      {/* Core Values */}
      <div className={cardClass}>
        {sectionHeader('values', editing === 'values')}

        {editing === 'values' ? (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {draftValues.map((v, i) => (
                <div
                  key={i}
                  className={`relative rounded-lg p-4 pt-8 border ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                >
                  <button
                    type="button"
                    onClick={() => setDraftValues((arr) => arr.filter((_, idx) => idx !== i))}
                    className="absolute top-1.5 right-1.5 p-1 rounded-md text-gray-400 hover:text-red-600"
                    title="Remove value"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <input
                    type="text"
                    value={v.title}
                    onChange={(e) => setDraftValues((arr) => arr.map((x, idx) => (idx === i ? { ...x, title: e.target.value } : x)))}
                    placeholder="Value name"
                    className={`w-full px-2 py-1.5 rounded-md border text-sm font-semibold text-center focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                  <textarea
                    value={v.description}
                    onChange={(e) => setDraftValues((arr) => arr.map((x, idx) => (idx === i ? { ...x, description: e.target.value } : x)))}
                    placeholder="Short description"
                    rows="4"
                    className={`mt-2 w-full px-2.5 py-2 rounded-md border text-sm leading-relaxed text-center resize-none focus:outline-none focus:ring-2 focus:ring-red-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                  />
                </div>
              ))}
              {draftValues.length < MAX_VALUES && (
                <button
                  type="button"
                  onClick={() => setDraftValues((arr) => [...arr, { title: '', description: '' }])}
                  className={`rounded-lg p-4 border border-dashed flex flex-col items-center justify-center gap-1 text-xs font-medium min-h-[112px] transition-colors ${
                    darkMode
                      ? 'border-gray-700 text-gray-400 hover:border-red-500 hover:text-red-400'
                      : 'border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-600'
                  }`}
                >
                  <Plus className="w-4 h-4" /> Add value
                </button>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button type="button" onClick={saveValues} disabled={saving} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          </div>
        ) : data.values.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.values.map((v, i) => (
              <div
                key={i}
                className={`rounded-lg p-4 text-center ${darkMode ? 'bg-gray-800/60' : 'bg-gray-50'}`}
              >
                <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{v.title}</p>
                {v.description && (
                  <p className={`text-xs leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{v.description}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic text-center">No values yet — add them or generate with AI.</p>
        )}
      </div>
    </div>

    {aiModal && (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={closeAiModal}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-xl p-6 ${
            darkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white'
          }`}
        >
          <div className="relative mb-4">
            <h3 className={`text-lg font-semibold text-center px-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Write {SECTION_META[aiModal].label} with AI
            </h3>
            <button
              type="button"
              onClick={closeAiModal}
              className="absolute top-0 right-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {AI_MODAL_META[aiModal].prompt}
          </label>
          <textarea
            value={aiInstructions}
            onChange={(e) => setAiInstructions(e.target.value)}
            rows="3"
            placeholder="A sentence or two is plenty…"
            className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'border-gray-300 text-gray-900'
            }`}
          />

          {aiModal !== 'values' && (
            <div className={`mt-3 rounded-lg p-3 text-sm ${darkMode ? 'bg-gray-800/60 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
              <span className="font-semibold">{AI_MODAL_META[aiModal].exampleLabel}: </span>
              <span className="italic">“{AI_MODAL_META[aiModal].example}”</span>
            </div>
          )}

          {aiModal === 'values' && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Need ideas? Tap to pick
                </span>
                <span className={`text-xs font-medium ${aiSelectedValues.length >= MAX_VALUES ? 'text-red-500' : 'text-gray-400'}`}>
                  {aiSelectedValues.length}/{MAX_VALUES} selected
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {EXAMPLE_VALUES.map((title) => {
                  const selected = aiSelectedValues.includes(title);
                  const atCap = !selected && aiSelectedValues.length >= MAX_VALUES;
                  return (
                    <button
                      key={title}
                      type="button"
                      onClick={() => toggleExampleValue(title)}
                      disabled={atCap}
                      className={`px-2 py-1.5 rounded-lg text-xs font-medium border text-center transition-colors ${
                        selected
                          ? 'bg-red-600 border-red-600 text-white'
                          : darkMode
                            ? 'bg-gray-800 border-gray-700 text-gray-200 hover:border-red-500 disabled:opacity-40 disabled:cursor-not-allowed'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-red-400 disabled:opacity-40 disabled:cursor-not-allowed'
                      }`}
                    >
                      {title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {error && <p className="text-xs text-red-600 dark:text-red-400 mt-3">{error}</p>}

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={closeAiModal}
              className="px-3 py-1.5 rounded-md text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAiGenerate}
              disabled={aiLoading === aiModal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            >
              {aiLoading === aiModal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CompanyDirectionCard;
